import React from 'react'
import { Box, styled } from '@mui/material'

import { pxToRem } from '@/shared/styles/themes'
import { PullQuoteBlock } from '@/slices/blog/mocks/articles'

export interface ArticlePullQuoteProps {
  block: PullQuoteBlock
}

export const ArticlePullQuote: React.FC<ArticlePullQuoteProps> = ({ block }) => {
  const { quote, attribution, attributionTitle } = block

  return (
    <PullQuoteWrapper>
      <QuoteMark>&ldquo;</QuoteMark>
      <QuoteBody>{quote}</QuoteBody>
      <AccentLine />
      {attribution && (
        <Attribution>
          <AttributionName>&mdash; {attribution}</AttributionName>
          {attributionTitle && (
            <AttributionRole>{attributionTitle}</AttributionRole>
          )}
        </Attribution>
      )}
    </PullQuoteWrapper>
  )
}

const PullQuoteWrapper = styled(Box)(({ theme }) => ({
  margin: `${pxToRem(44)} ${pxToRem(-8)}`,
  padding: `${pxToRem(32)} ${pxToRem(24)}`,
  textAlign: 'center',
  position: 'relative',

  [theme.breakpoints.up('md')]: {
    margin: `${pxToRem(56)} ${pxToRem(-24)}`,
    padding: `${pxToRem(40)} ${pxToRem(32)}`,
  },
}))

const QuoteMark = styled('span')(({ theme }) => ({
  display: 'block',
  fontSize: pxToRem(72),
  fontWeight: 700,
  lineHeight: 0.6,
  color: '#4A7FA5',
  marginBottom: pxToRem(8),

  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(96),
  },
}))

const QuoteBody = styled('p')(({ theme }) => ({
  fontSize: pxToRem(24),
  fontWeight: 200,
  color: theme.palette.primary.main,
  lineHeight: 1.5,
  margin: 0,
  maxWidth: pxToRem(640),
  marginLeft: 'auto',
  marginRight: 'auto',

  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(32),
  },
}))

const AccentLine = styled('span')(({ theme }) => ({
  display: 'block',
  width: pxToRem(40),
  height: pxToRem(2),
  backgroundColor: theme.palette.custom.terracotta,
  margin: `${pxToRem(24)} auto`,

  [theme.breakpoints.up('md')]: {
    margin: `${pxToRem(28)} auto`,
  },
}))

const Attribution = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: pxToRem(4),
})

const AttributionName = styled('span')(({ theme }) => ({
  fontSize: pxToRem(14),
  fontWeight: 600,
  color: theme.palette.primary.main,

  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(15),
  },
}))

const AttributionRole = styled('span')(({ theme }) => ({
  fontSize: pxToRem(13),
  fontWeight: 400,
  color: theme.palette.text.secondary,

  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(14),
  },
}))
